
import '../../styles/_main-error.scss';

import React, { PropsWithChildren } from 'react';

import { useAppSelector } from '../../hooks';
import { ModalPanel } from '../ModalPanel/ModalPanel';

export enum ErrorContainer {
  Standalone,
  PlayerModal,
  SmallPanel,
  LargePanel
}

export type IMainErrorProps = PropsWithChildren<{
  noHelpInfo?: boolean;
  errorContainer: ErrorContainer;
  marginRequired?: boolean;

  /** Optional custom title for non-standalone containers */
  title?: string;

  /** Optional message as JSX (preferred for internal links or formatted content) */
  message?: React.ReactNode;

  /** Optional message as a raw HTML string (rendered via dangerouslySetInnerHTML) */
  htmlMessage?: string;
}>;

function reloadPage() {
    window.location.reload();
}

export function MainError({
    noHelpInfo,
    errorContainer,
    marginRequired = false,
    children,
    title = 'Sorry, that didn’t work',
    message,
    htmlMessage
}: IMainErrorProps) {
    const clientConfig = useAppSelector((state) => state.clientConfig);


    const helpMessage = (
        <>
      For more help, contact customer service at{' '}
            <a href="tel:8002604754">800.260.475.4</a> or visit our{' '}
            <a href={clientConfig?.pliHelpCenterUrl ?? ''}>help center</a>.
        </>
    );

    const marginTop = marginRequired ? 'mt-4' : 'mt-0';

    /**
   * Determines which message block to render:
   * 1) If `message` (ReactNode) is provided, render it directly.
   * 2) If `htmlMessage` (string) is provided, render it as HTML safely.
   * 3) Otherwise, fallback to the default "reload the page" text.
   */
    const renderMessageBlock = () => {
        if (message) return <>{message}</>;
        if (htmlMessage)
            return <div dangerouslySetInnerHTML={{ __html: htmlMessage }} />;
        return (
            <p className="paragraph-1 mb-0">
        Please try{' '}
                <button className="main-error main-error--link_button" onClick={reloadPage}>
          reloading the page
                </button>
        .
            </p>
        );
    };

    return (
        <>
            {errorContainer === ErrorContainer.Standalone && (
                <ModalPanel isOpen noNav className="watch-modal--standalone d-flex justify-content-center">
                    <div className="watch-modal__content watch-modal__content--slim text-center">
                        <div className="main-error main-error--standalone text--white">
                            {/* For backward compatibility: prefer children if provided, otherwise use the new message props */}
                            {children ?? renderMessageBlock()}
                            {!noHelpInfo && clientConfig && (
                                <>
                                    <br />
                                    <small>{helpMessage}</small>
                                </>
                            )}
                        </div>
                    </div>
                </ModalPanel>
            )}

            {(errorContainer === ErrorContainer.SmallPanel ||
        errorContainer === ErrorContainer.PlayerModal ||
        errorContainer === ErrorContainer.LargePanel) && (
                <div className={`main-error main-error--component ${marginTop}`}>
                    <h1 className={`${errorContainer === ErrorContainer.SmallPanel ? 'heading-5' : 'heading-4'} mb-0`}>
                        {title}
                    </h1>

                    {/* Prefer children (old behavior); otherwise render message or fallback */}
                    {children ? (
                        <div className="paragraph-1 mb-0">{children}</div>
                    ) : (
                        renderMessageBlock()
                    )}

                    {!noHelpInfo && <p className="paragraph-1 mb-0">{helpMessage}</p>}
                </div>
            )}
        </>
    );
}
